import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getValidAccessToken } from '../../store/oauth-browser.js';
import { BASE_PATH } from '../../store';
import { headerViewStyles } from 'src/layout/parts/header-view-styles';
import { t } from 'src/i18n';

interface CalendarLink {
  name: string;
  description: string;
  url: string;
}

/**
 * Calendar settings component with external calendar integration
 */
@customElement('d-calendar-settings')
export class DCalendarSettings extends LitElement {
  static readonly styles = [
    headerViewStyles,
    css`
      :host {
        display: block;
      }

      .help-links {
        margin-bottom: 24px;
        padding: 12px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
      }

      .help-links a {
        color: black;
        text-decoration: none;
        margin-right: 16px;
        font-size: 13px;
        opacity: 0.9;
      }

      .help-links a:hover {
        text-decoration: underline;
        opacity: 1;
      }

      .calendar-links-list {
        margin-top: 20px;
      }

      .calendar-link-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px;
        margin-bottom: 8px;
        background: rgba(255, 255, 255, 0.15);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 4px;
      }

      .calendar-link-info {
        flex: 1;
        min-width: 0;
      }

      .calendar-link-description {
        font-size: 14px;
        font-weight: 500;
        color: black;
        margin-bottom: 4px;
      }

      .calendar-link-url {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.7);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-family: monospace;
      }

      .copy-button {
        flex: none;
        margin-left: 12px;
        padding: 6px 12px;
        background: rgba(255, 255, 255, 0.2);
        color: black;
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 4px;
        transition: all 0.2s;
      }

      .copy-button:hover {
        background: rgba(255, 255, 255, 0.3);
        border-color: rgba(0, 0, 0, 0.3);
      }

      .copy-button.copied {
        background: rgba(76, 175, 80, 0.3);
        border-color: rgba(76, 175, 80, 0.5);
      }

      .copy-icon {
        width: 14px;
        height: 14px;
      }

      .loading {
        text-align: center;
        padding: 20px;
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
      }

      .error {
        padding: 12px;
        background: rgba(255, 0, 0, 0.1);
        color: black;
        border: 1px solid rgba(255, 0, 0, 0.3);
        border-radius: 4px;
        margin: 16px 0;
        font-size: 13px;
      }

      .no-links {
        text-align: center;
        padding: 32px 16px;
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
      }
    `,
  ];

  @property({ type: Number })
  organizationId = 0;

  @property({ type: Array })
  features: string[] = [];

  @state()
  private calendarLinks: CalendarLink[] = [];

  @state()
  private isLoading = false;

  @state()
  private error = '';

  @state()
  private copiedUrl: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.features.includes('EXTERNAL_CALENDAR')) {
      this.fetchCalendarLinks();
    }
  }

  render() {
    // Debug: Log features to see what's available
    console.log('Calendar settings - features:', this.features);

    // Only show external calendar settings if EXTERNAL_CALENDAR feature is enabled
    if (!this.features || !this.features.includes('EXTERNAL_CALENDAR')) {
      return html``;
    }

    return html`
      <div class="settings-section">
        <h2>${t('layout.calendarSettings.heading')}</h2>
        <p>${t('layout.calendarSettings.intro')}</p>

        <div class="help-links">
          <a href="https://support.google.com/calendar/answer/37100" target="_blank" rel="noopener">
            📅 Google Calendar
          </a>
          <a
            href="https://support.apple.com/guide/iphone/use-multiple-calendars-iph3d1110d4/ios"
            target="_blank"
            rel="noopener"
          >
            📱 iPhone/iPad
          </a>
          <a
            href="https://support.microsoft.com/en-us/office/import-or-subscribe-to-a-calendar-in-outlook-cff1429c-5af6-41ec-a5b4-74f2c278e98c"
            target="_blank"
            rel="noopener"
          >
            💼 Outlook
          </a>
        </div>

        ${this.isLoading
          ? html`<div class="loading">${t('layout.calendarSettings.loading')}</div>`
          : this.error
            ? html`<div class="error">${this.error}</div>`
            : this.calendarLinks.length > 0
              ? html`
                  <div class="calendar-links-list">
                    ${this.calendarLinks.map(
                      (link) => html`
                        <div class="calendar-link-item">
                          <div class="calendar-link-info">
                            <div class="calendar-link-description">${link.name}</div>
                            <div class="calendar-link-url" title="${link.url}">${link.description}</div>
                          </div>
                          <button
                            class="copy-button ${this.copiedUrl === link.url ? 'copied' : ''}"
                            @click=${() => this.copyToClipboard(link)}
                            title="${t('layout.calendarSettings.copyLink')}"
                          >
                            ${this.copiedUrl === link.url
                              ? html`${this.renderCheckIcon()} ${t('layout.calendarSettings.copied')}`
                              : html`${this.renderCopyIcon()} ${t('layout.calendarSettings.copy')}`}
                          </button>
                        </div>
                      `,
                    )}
                  </div>
                `
              : html`<div class="no-links">${t('layout.calendarSettings.noLinks')}</div>`}
      </div>
    `;
  }

  private async fetchCalendarLinks() {
    this.isLoading = true;
    this.error = '';

    try {
      // Get valid access token
      const token = await getValidAccessToken();

      if (!token) {
        throw new Error('No valid access token available');
      }

      const response = await fetch(`${BASE_PATH}/organization/${this.organizationId}/calendar-links`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch calendar links: ${response.statusText}`);
      }

      const data = await response.json();

      // The API already returns full URLs in the CalendarLink objects
      this.calendarLinks = data;
    } catch (error) {
      console.error('Error fetching calendar links:', error);
      this.error = t('layout.calendarSettings.fetchError');
    } finally {
      this.isLoading = false;
    }
  }

  private async copyToClipboard(link: CalendarLink) {
    if (!link.url) return;

    try {
      await navigator.clipboard.writeText(link.url);
      this.copiedUrl = link.url;

      // Reset copied state after 2 seconds
      setTimeout(() => {
        this.copiedUrl = null;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }

  private renderCopyIcon() {
    return html`
      <svg class="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
    `;
  }

  private renderCheckIcon() {
    return html`
      <svg class="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-calendar-settings': DCalendarSettings;
  }
}
